import {format} from 'date-fns';
import {combine, createEvent, merge, sample} from 'effector';
import {createGate} from 'effector-react';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {dateModel} from '@entities/date/model';
import {InnerFiltersElement, InnerSortingsElement} from '@shared/types/grid';
import {SelectOption} from '@shared/ui/Select';

import * as api from './api';
import {
    DictionarySearchResponse,
    JobDictionaryItem,
    JobExecutionFilterNode,
    JobExecutionSearchResponse,
} from './types';

const domain = rootDomain.createDomain('AuditJobs');

export const AuditJobsGate = createGate('AuditJobs');

const fetchJobExecutionsFx = domain.createEffect({handler: api.fetchJobExecutions});
const fetchJobExecutionStatusesFx = domain.createEffect({
    handler: api.fetchJobExecutionStatuses,
});

[fetchJobExecutionsFx, fetchJobExecutionStatusesFx].forEach((fx) =>
    fx.failData.watch(showRestError),
);

const data$ = domain
    .createStore<JobExecutionSearchResponse | null>(null)
    .on(fetchJobExecutionsFx.doneData, (_, data) => data)
    .reset(AuditJobsGate.close);

const statuses$ = domain
    .createStore<JobDictionaryItem[]>([])
    .on(
        fetchJobExecutionStatusesFx.doneData,
        (_, data: DictionarySearchResponse) => data.data ?? [],
    )
    .reset(AuditJobsGate.close);

const search$ = domain.createStore('').reset(AuditJobsGate.close);
const statusCode$ = domain.createStore<string | null>(null).reset(AuditJobsGate.close);
const sortings$ = domain
    .createStore<Record<string, boolean | undefined>>({updated: true})
    .reset(AuditJobsGate.close);
const pageNumber$ = domain.createStore(0).reset(AuditJobsGate.close);
const pageSize$ = domain.createStore(10).reset(AuditJobsGate.close);

export const searchChanged = createEvent<string>();
export const statusCodeChanged = createEvent<string | null>();
export const sortingsChanged = createEvent<InnerSortingsElement[]>();
export const pageNumberChanged = createEvent<number>();
export const pageSizeChanged = createEvent<number>();

search$.on(searchChanged, (_, value) => value);
statusCode$.on(statusCodeChanged, (_, value) => value);
sortings$.on(sortingsChanged, (state, sortings) => ({
    ...state,
    ...Object.fromEntries(sortings.map(({id, value}) => [id, value])),
}));
pageNumber$.on(pageNumberChanged, (_, page) => page);
pageSize$.on(pageSizeChanged, (_, size) => size);
pageNumber$.reset(searchChanged, statusCodeChanged, pageSizeChanged);

const buildFilter = ({
    from,
    to,
    search,
    statusCode,
}: {
    from: Date | null;
    to: Date | null;
    search: string;
    statusCode: string | null;
}): JobExecutionFilterNode => {
    const children: JobExecutionFilterNode[] = [
        {type: 'EQ', field: 'jobType.code', values: ['FLOW']},
    ];

    if (from && to) {
        children.push({
            type: 'BTW',
            field: 'created',
            values: [format(from, 'yyyy-MM-dd'), format(to, 'yyyy-MM-dd')],
        });
    }

    const trimmedSearch = search.trim();
    if (trimmedSearch) {
        children.push({type: 'ILIKE', field: 'jobInfo.title', values: [`%${trimmedSearch}%`]});
    }

    if (statusCode) {
        children.push({type: 'EQ', field: 'status.code', values: [statusCode]});
    }

    return children.length === 1 ? children[0] : {type: 'AND', children};
};

const mapSorts = (sortings: Record<string, boolean | undefined>) => {
    const [field, value] = Object.entries(sortings).find(([, sort]) => sort != null) ?? [];
    if (!field || value == null) return null;
    return [{field, order: value ? 'DESC' : 'ASC'} as const];
};

export const filtersChanged = createEvent<InnerFiltersElement[]>();
filtersChanged.watch((filters) => {
    const {id, value} = filters[0] ?? {};
    if (id === 'status') {
        statusCodeChanged(((value as SelectOption<string> | null)?.value ?? null) as string | null);
    }
});

export const filtersReset = createEvent<string[]>();
filtersReset.watch((ids) => {
    if (ids[0] === 'status') statusCodeChanged(null);
});

sample({
    clock: AuditJobsGate.open,
    target: fetchJobExecutionStatusesFx,
});

sample({
    clock: merge([
        AuditJobsGate.open,
        dateModel.changed,
        searchChanged,
        statusCodeChanged,
        sortingsChanged,
        pageNumberChanged,
        pageSizeChanged,
    ]),
    source: combine({
        date: dateModel.state,
        search: search$,
        statusCode: statusCode$,
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
    }),
    filter: AuditJobsGate.status,
    fn: ({date, search, statusCode, sortings, pageNumber, pageSize}) => ({
        filter: buildFilter({
            from: date.range.from,
            to: date.range.to,
            search,
            statusCode,
        }),
        sorts: mapSorts(sortings),
        page: {page: pageNumber, size: pageSize},
    }),
    target: fetchJobExecutionsFx,
});

const toOption = (item: JobDictionaryItem): SelectOption<string> => ({
    value: item.code,
    label: item.title?.en ?? Object.values(item.title ?? {})[0] ?? item.code,
});

const filters$ = combine(
    {
        statusCode: statusCode$,
        statuses: statuses$,
    },
    ({statusCode, statuses}) => ({
        status: statusCode
            ? toOption(
                  statuses.find((item) => item.code === statusCode) ??
                      ({code: statusCode} as JobDictionaryItem),
              )
            : undefined,
    }),
);

const pending$ = combine(
    [fetchJobExecutionsFx.pending, fetchJobExecutionStatusesFx.pending],
    (list) => list.some(Boolean),
);

const $auditJobsState = combine({
    data: data$,
    statuses: statuses$,
    search: search$,
    filters: filters$,
    sortings: sortings$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    pending: pending$,
});

export const auditJobsModel = {
    state: $auditJobsState,
    searchChanged,
    filtersChanged,
    filtersReset,
    sortingsChanged,
    pageNumberChanged,
    pageSizeChanged,
};
